import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { rupiah } from "../lib/api";
import {
  LayoutDashboard, ShoppingCart, Package, Tags, Boxes, Receipt, BarChart3,
  Users, Store, UserCog, CreditCard, Settings, User, LogOut, Menu, X, Building2, Crown,
} from "lucide-react";

const STORE_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, key: "dash" },
  { to: "/pos", label: "Kasir (POS)", icon: ShoppingCart, key: "pos" },
  { to: "/transactions", label: "Riwayat Transaksi", icon: Receipt, key: "txn" },
  { to: "/products", label: "Produk", icon: Package, key: "products", perm: "can_manage_products" },
  { to: "/categories", label: "Kategori", icon: Tags, key: "categories", perm: "can_manage_products" },
  { to: "/stock", label: "Stok Barang", icon: Boxes, key: "stock", perm: "can_manage_products" },
  { to: "/customers", label: "Pelanggan", icon: Users, key: "customers", perm: "can_manage_customers" },
  { to: "/reports", label: "Laporan", icon: BarChart3, key: "reports", perm: "can_view_reports" },
  { to: "/staff", label: "Kasir & Staff", icon: UserCog, key: "staff", ownerOnly: true },
  { to: "/subscription", label: "Langganan", icon: CreditCard, key: "sub", ownerOnly: true },
  { to: "/settings", label: "Pengaturan Toko", icon: Settings, key: "settings", ownerOnly: true },
  { to: "/profile", label: "Profil", icon: User, key: "profile" },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Ringkasan", icon: LayoutDashboard, end: true },
  { to: "/admin/tenants", label: "Toko Pelanggan", icon: Building2 },
  { to: "/admin/profile", label: "Profil", icon: User },
];

function canSee(item, user) {
  if (user.role === "owner") return true;
  if (item.ownerOnly) return false;
  if (item.perm) return !!user.permissions?.[item.perm];
  return true;
}

export default function DashboardLayout({ admin }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = admin ? ADMIN_NAV : STORE_NAV.filter((i) => canSee(i, user));
  const dark = admin;

  const roleLabel = { super_admin: "Super Admin", owner: "Pemilik Toko", cashier: "Kasir" }[user.role];

  const doLogout = async () => { await logout(); navigate("/login"); };

  const sidebarBase = dark ? "bg-slate-900 text-slate-100 border-slate-800" : "bg-white text-slate-700 border-slate-200";

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-screen w-72 border-r flex flex-col transition-transform duration-200 ${sidebarBase} ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className={`h-16 flex items-center gap-2 px-6 border-b ${dark ? "border-slate-800" : "border-slate-200"}`}>
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center text-white">
            {dark ? <Crown className="w-5 h-5" /> : <Store className="w-5 h-5" />}
          </div>
          <div>
            <div className="font-heading font-extrabold text-lg leading-none">KASIRKU</div>
            <div className={`text-[10px] uppercase tracking-widest ${dark ? "text-slate-400" : "text-slate-400"}`}>
              {admin ? "Admin Panel" : "Point of Sale"}
            </div>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)} data-testid="close-sidebar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto kasir-scroll py-4 px-3 space-y-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.key || item.to.replace(/\//g, "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-white shadow-sm"
                    : dark
                    ? "text-slate-300 hover:bg-slate-800"
                    : "text-slate-600 hover:bg-accent hover:text-accent-foreground"
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px]" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={`p-3 border-t ${dark ? "border-slate-800" : "border-slate-200"}`}>
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold overflow-hidden">
              {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user.name?.[0] || "U")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user.name}</div>
              <div className={`text-xs ${dark ? "text-slate-400" : "text-slate-400"}`}>{roleLabel}</div>
            </div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className={`mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${dark ? "text-red-300 hover:bg-slate-800" : "text-red-600 hover:bg-red-50"}`}
          >
            <LogOut className="w-[18px] h-[18px]" /> Keluar
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 flex items-center gap-3 px-4 sm:px-6 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-20">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-sidebar">
            <Menu className="w-6 h-6" />
          </button>
          <div className="font-heading font-bold text-slate-800 hidden sm:block">
            {admin ? "Panel Super Admin" : "Manajemen Toko"}
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
